/* eslint-disable max-lines-per-function, complexity, max-len, indent, jsx-a11y/label-has-associated-control */
import React, { useMemo, useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Trash2,
  Shield,
  ArrowRight,
  Activity,
  Clock,
  UserPlus
} from 'lucide-react';
import { format } from 'date-fns';
import { AuditLog } from '@/types';

type AuditLogRow = {
  id: string;
  tenant_id: string | null;
  user_id: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  created_at: string;
};

const USER_SETUP_AUDIT_QUERY_KEY = ['admin', 'user-setup-audit-logs'] as const;

export const UserAuditTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const {
    data: rawRows,
    isLoading: loading,
    error: queryError
  } = useQuery({
    queryKey: USER_SETUP_AUDIT_QUERY_KEY,
    queryFn: async (): Promise<AuditLogRow[]> => {
      const { data, error } = await supabase.rpc('get_user_setup_audit_logs_for_admin', {
        p_limit: 500
      });
      if (error) throw error;
      return (data || []) as AuditLogRow[];
    }
  });

  const auditLogs: AuditLog[] = useMemo(() => {
    const rows = rawRows || [];
    return rows.map(row => {
      const resourceName =
        (row.new_values?.['email'] as string | undefined) ||
        (row.new_values?.['resource_name'] as string | undefined) ||
        (row.old_values?.['email'] as string | undefined) ||
        (row.old_values?.['resource_name'] as string | undefined) ||
        undefined;

      const success = !row.action.includes('error') && !row.action.includes('failed');

      const entry: AuditLog = {
        id: row.id,
        createdAt: row.created_at,
        updatedAt: row.created_at,
        action: row.action,
        resourceType: row.resource_type ?? '',
        success,
        metadata: {
          tenant_id: row.tenant_id
        },
        ...(row.user_id ? { userId: row.user_id } : {}),
        ...(row.tenant_id ? { organizationId: row.tenant_id } : {}),
        ...(row.resource_id ? { resourceId: row.resource_id } : {}),
        ...(resourceName ? { resourceName } : {}),
        ...(row.old_values != null ? { oldValues: row.old_values } : {}),
        ...(row.new_values != null ? { newValues: row.new_values } : {})
      };
      return entry;
    });
  }, [rawRows]);

  const userIdsForEmail = useMemo(() => {
    const ids = new Set<string>();
    for (const r of rawRows || []) {
      if (r.user_id) ids.add(r.user_id);
    }
    return [...ids];
  }, [rawRows]);

  const { data: actorEmailMap = {} } = useQuery({
    queryKey: [
      ...USER_SETUP_AUDIT_QUERY_KEY,
      'actor-emails',
      [...userIdsForEmail].sort().join(',')
    ],
    queryFn: async (): Promise<Record<string, string>> => {
      if (userIdsForEmail.length === 0) return {};
      const { data: usersData, error } = await supabase
        .from('users')
        .select('id, email')
        .in('id', userIdsForEmail);
      if (error) throw error;
      const map: Record<string, string> = {};
      (usersData || []).forEach(u => {
        map[u.id] = u.email;
      });
      return map;
    },
    enabled: userIdsForEmail.length > 0
  });

  type LogWithActor = AuditLog & { _actorEmail?: string };

  const auditLogsWithActors: LogWithActor[] = useMemo(() => {
    return auditLogs.map(log => {
      const email = log.userId ? actorEmailMap[log.userId] : undefined;
      return {
        ...log,
        ...(email ? { _actorEmail: email } : {})
      };
    });
  }, [auditLogs, actorEmailMap]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, actionFilter]);

  const filteredLogs = auditLogsWithActors.filter(log => {
    const matchesSearch =
      !searchTerm ||
      (log.resourceName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      String(log.newValues?.['full_name'] ?? '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(log.oldValues?.['full_name'] ?? '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(log.newValues?.['email'] ?? '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(log.oldValues?.['email'] ?? '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const actionMatches =
      actionFilter === 'all' ||
      log.action === actionFilter ||
      (actionFilter === 'user.approved' && log.action === 'user_approval_approved') ||
      (actionFilter === 'user.rejected' && log.action === 'user_approval_rejected') ||
      (actionFilter === 'user.removed' &&
        (log.action === 'user.removed' || log.action === 'user_approval_deleted'));

    return matchesSearch && actionMatches;
  });

  const totalPages = Math.ceil(filteredLogs.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'user.approved':
      case 'user_approval_approved':
        return (
          <Badge className='bg-green-100 text-green-800 hover:bg-green-200 border-green-200'>
            <CheckCircle className='w-3 h-3 mr-1' /> Approved
          </Badge>
        );
      case 'user.rejected':
      case 'user_approval_rejected':
        return (
          <Badge className='bg-red-100 text-red-800 hover:bg-red-200 border-red-200'>
            <XCircle className='w-3 h-3 mr-1' /> Rejected
          </Badge>
        );
      case 'user.role_changed':
        return (
          <Badge className='bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-200'>
            <RefreshCw className='w-3 h-3 mr-1' /> Role Change
          </Badge>
        );
      case 'user.removed':
      case 'user_approval_deleted':
      case 'user.deleted':
        return (
          <Badge className='bg-orange-100 text-orange-800 hover:bg-orange-200 border-orange-200'>
            <Trash2 className='w-3 h-3 mr-1' /> Removed
          </Badge>
        );
      case 'user_approval_created':
      case 'user.created':
        return (
          <Badge className='bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-200'>
            {action === 'user.created' ? (
              <UserPlus className='w-3 h-3 mr-1' />
            ) : (
              <Clock className='w-3 h-3 mr-1' />
            )}
            {action === 'user.created' ? 'Created (invite)' : 'Created'}
          </Badge>
        );
      case 'user_approval_updated':
        return (
          <Badge className='bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border-yellow-200'>
            <RefreshCw className='w-3 h-3 mr-1' /> Updated
          </Badge>
        );
      case 'user.tenant_switched':
        return (
          <Badge className='bg-purple-100 text-purple-800 hover:bg-purple-200 border-purple-200'>
            <ArrowRight className='w-3 h-3 mr-1' /> Tenant switched
          </Badge>
        );
      default:
        return <Badge variant='outline'>{action}</Badge>;
    }
  };

  const renderDetails = (log: LogWithActor) => {
    if (log.action === 'user.role_changed') {
      return (
        <div className='flex items-center gap-2 text-sm'>
          <span className='text-muted-foreground line-through'>
            {String(log.oldValues?.['role_name'] ?? 'No Role')}
          </span>
          <ArrowRight className='w-3 h-3 text-muted-foreground' />
          <span className='font-medium'>{String(log.newValues?.['role_name'] ?? '')}</span>
        </div>
      );
    }

    if (log.action === 'user.approved' || log.action === 'user_approval_approved') {
      return (
        <div className='flex items-center gap-2 text-sm'>
          <span className='text-muted-foreground'>Role:</span>
          <span className='font-medium'>
            {String(log.newValues?.['role_name'] ?? log.newValues?.['status'] ?? 'None')}
          </span>
        </div>
      );
    }

    if (log.action === 'user.rejected' || log.action === 'user_approval_rejected') {
      return <div className='text-sm text-red-600'>Access denied</div>;
    }

    if (log.action === 'user.removed' || log.action === 'user_approval_deleted') {
      return <div className='text-sm text-red-600'>Access revoked & roles deactivated</div>;
    }

    if (log.action === 'user.deleted') {
      return <div className='text-sm text-red-600'>User deleted from system</div>;
    }

    if (log.action === 'user.created') {
      return (
        <div className='flex flex-col gap-0.5 text-sm'>
          {log.newValues?.['role_name'] != null && (
            <span>
              <span className='text-muted-foreground'>Role:</span>{' '}
              <span className='font-medium'>{String(log.newValues['role_name'])}</span>
            </span>
          )}
          {log.newValues?.['created_via'] != null && (
            <span className='text-xs text-muted-foreground'>
              Via {String(log.newValues['created_via'])}
            </span>
          )}
        </div>
      );
    }

    if (log.action === 'user.tenant_switched') {
      return (
        <div className='flex items-center gap-2 text-sm'>
          <span className='text-muted-foreground'>Tenant:</span>
          <span className='font-medium'>{String(log.newValues?.['tenant_name'] ?? 'Unknown')}</span>
        </div>
      );
    }

    if (log.action === 'user.tenant_added' || log.action === 'user.tenant_removed') {
      return (
        <div className='flex items-center gap-2 text-sm'>
          <span className='text-muted-foreground'>Tenant:</span>
          <span className='font-medium'>{String(log.newValues?.['tenant_name'] ?? 'Unknown')}</span>
        </div>
      );
    }

    return <span className='text-sm text-muted-foreground'>-</span>;
  };

  const stats = {
    total: auditLogsWithActors.length,
    approvals: auditLogsWithActors.filter(
      l => l.action === 'user.approved' || l.action === 'user_approval_approved'
    ).length,
    roleChanges: auditLogsWithActors.filter(l => l.action === 'user.role_changed').length,
    removals: auditLogsWithActors.filter(
      l =>
        l.action === 'user.removed' ||
        l.action === 'user_approval_deleted' ||
        l.action === 'user.deleted'
    ).length
  };

  const errorMessage =
    queryError instanceof Error ? queryError.message : queryError ? String(queryError) : null;

  return (
    <div className='space-y-6 h-full flex flex-col'>
      {errorMessage && (
        <div className='rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive'>
          Could not load audit logs: {errorMessage}
          {/get_user_setup_audit_logs|does not exist|42883/i.test(errorMessage) ? (
            <span className='block mt-1 text-muted-foreground'>
              Apply the migration{' '}
              <code className='text-xs'>create-get-user-setup-audit-logs-rpc.sql</code> if this RPC
              is missing.
            </span>
          ) : null}
        </div>
      )}

      <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Total Actions</CardTitle>
            <Activity className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{stats.total}</div>
            <p className='text-xs text-muted-foreground'>Recorded lifecycle events</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Approvals</CardTitle>
            <CheckCircle className='h-4 w-4 text-green-500' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{stats.approvals}</div>
            <p className='text-xs text-muted-foreground'>Users approved</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Role Changes</CardTitle>
            <RefreshCw className='h-4 w-4 text-blue-500' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{stats.roleChanges}</div>
            <p className='text-xs text-muted-foreground'>Role updates</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Removals</CardTitle>
            <Trash2 className='h-4 w-4 text-orange-500' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{stats.removals}</div>
            <p className='text-xs text-muted-foreground'>Users removed / deleted</p>
          </CardContent>
        </Card>
      </div>

      <div className='bg-card border rounded-lg p-4 space-y-4'>
        <div className='flex flex-col md:flex-row gap-4 items-end'>
          <div className='flex-1 space-y-2 w-full'>
            <label className='text-xs font-medium text-muted-foreground'>Search Users</label>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
              <Input
                placeholder='Search by email or name...'
                className='pl-9'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className='w-full md:w-[200px] space-y-2'>
            <label className='text-xs font-medium text-muted-foreground'>Action Type</label>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger>
                <SelectValue placeholder='All Actions' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Actions</SelectItem>
                <SelectItem value='user.approved'>Approved</SelectItem>
                <SelectItem value='user.rejected'>Rejected</SelectItem>
                <SelectItem value='user.created'>User created (invite)</SelectItem>
                <SelectItem value='user.role_changed'>Role Changed</SelectItem>
                <SelectItem value='user.tenant_switched'>Tenant Switched</SelectItem>
                <SelectItem value='user.tenant_added'>Tenant Added</SelectItem>
                <SelectItem value='user.tenant_removed'>Tenant Removed</SelectItem>
                <SelectItem value='user.deleted'>Deleted</SelectItem>
                <SelectItem value='user.removed'>Removed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant='outline'
            onClick={() =>
              void queryClient.invalidateQueries({ queryKey: [...USER_SETUP_AUDIT_QUERY_KEY] })
            }
            className='mb-[2px]'
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className='border rounded-lg bg-card overflow-hidden flex-1'>
        <div className='overflow-x-auto'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Performed By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className='text-center py-12 text-muted-foreground'>
                    Loading audit logs...
                  </TableCell>
                </TableRow>
              ) : paginatedLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className='text-center py-12 text-muted-foreground'>
                    No audit logs found matching your criteria
                  </TableCell>
                </TableRow>
              ) : (
                paginatedLogs.map(log => (
                  <TableRow key={log.id}>
                    <TableCell className='whitespace-nowrap'>
                      <div className='flex flex-col'>
                        <span className='font-medium'>
                          {format(new Date(log.createdAt), 'MMM d, yyyy')}
                        </span>
                        <span className='text-xs text-muted-foreground'>
                          {format(new Date(log.createdAt), 'h:mm a')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{getActionBadge(log.action)}</TableCell>
                    <TableCell>
                      <div className='flex flex-col'>
                        <span className='font-medium'>
                          {log.resourceName ||
                            String(log.newValues?.['email'] ?? '') ||
                            String(log.oldValues?.['email'] ?? '') ||
                            'Unknown'}
                        </span>
                        {(log.newValues?.['full_name'] || log.oldValues?.['full_name']) && (
                          <span className='text-xs text-muted-foreground'>
                            {String(
                              log.newValues?.['full_name'] ?? log.oldValues?.['full_name'] ?? ''
                            )}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{renderDetails(log)}</TableCell>
                    <TableCell>
                      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                        <Shield className='h-3 w-3' />
                        <span className='truncate max-w-[150px]' title={log.userId}>
                          {log._actorEmail || 'System Admin'}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className='flex items-center justify-between pt-4'>
          <div className='text-sm text-muted-foreground'>
            Showing {startIndex + 1} to {Math.min(endIndex, filteredLogs.length)} of{' '}
            {filteredLogs.length} entries
          </div>
          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <div className='text-sm font-medium'>
              Page {currentPage} of {totalPages}
            </div>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
